import { Box, Button, Fade, Slide, Divider, Typography, useTheme, Popper, ClickAwayListener, Paper, Radio, Checkbox, CircularProgress, Skeleton } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'
import { useSearchParams } from 'react-router-dom'
import Confetti from 'react-confetti-boom'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import React from 'react'

import celebratingSimba from '../../../assets/simba-ai/celebration.png'
import thinkingSimba from '../../../assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import sadSimba from '../../../assets/simba-ai/sad.png'
import { Icons } from '../../../utils/utilities'
import AIWaitingScreen from '../AIWaitingScreen'
import ReportIssue from './ReportIssue'
import CustomChip from '../../common/CustomChip'
import Markdown from '../../../shared/components/common/custom-markdown/Markdown'
import { CustomCodeBlockReadOnly } from '../../../shared/components/common/custom-markdown/CustomCodeBlock'



const Renderer = observer( () => {

    const { layout, palette, border, theme } = useTheme()
    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = () => {
        SimbaAIStore.cancelQuestionStream()
        SimbaAIStore.setView( "home", "right" )
        setSearchParamsValues( { simbaAIOpen: 'false', practiceQuiz: "", simbaAIMode: "" } )
    }

    const nextQuestion = () => {
        const { expectedTotal } = SimbaAIStore.getQuestionStream
        const totalQuestions = expectedTotal || SimbaAIStore.getPracticeSessionQuestions.length
        let nextIndex = SimbaAIStore.getQuizState.questionIndex + 1
        if ( nextIndex >= totalQuestions )
            return
        SimbaAIStore.setQuizState( { questionIndex: nextIndex } )
    }

    const prevQuestion = () => {
        let prevIndex = SimbaAIStore.getQuizState.questionIndex - 1
        if ( prevIndex < 0 )
            return
        SimbaAIStore.setQuizState( { questionIndex: prevIndex } )
    }

    const selectAnswer = async answer => {
        const { questionIndex, userAnswers } = SimbaAIStore.getQuizState

        const question = SimbaAIStore.getPracticeSessionQuestions[SimbaAIStore.getQuizState.questionIndex]

        const newAnswers = [...userAnswers]

        if ( question.multiple_answers || question.correct_answers.length > 1 ) {

            if ( !Array.isArray( newAnswers[questionIndex] ) )
                newAnswers[questionIndex] = []

            const answerIndex = newAnswers[questionIndex].findIndex( ans => ans === answer )
            if ( answerIndex >= 0 ) {
                newAnswers[questionIndex].splice( answerIndex, 1 )
            } else {
                newAnswers[questionIndex].push( answer )
            }

            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        } else {
            newAnswers[questionIndex] = answer
            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        }
    }

    const submitAndViewScores = async () => {
        try {
            const { userAnswers } = SimbaAIStore.getQuizState

            const questions = SimbaAIStore.getPracticeSessionQuestions
            let score = 0
            for ( const [i, answer] of userAnswers.entries() ) {
                if ( !answer ) return

                const question = questions[i]

                let provided

                const correctAnswers = new Set( question.correct_answers )

                if ( Array.isArray( answer ) ) {
                    provided = new Set( answer )
                } else {
                    provided = new Set( [answer] )
                }

                const difference = provided.symmetricDifference( correctAnswers )

                if ( difference.size === 0 )
                    score += 1

            }
            SimbaAIStore.setQuizState( { attemptDetails: { ...SimbaAIStore.getQuizState.attemptDetails, score, user_answers: userAnswers } } )
            SimbaAIStore.playSound( "practiceCompleted" )
            SimbaAIStore.setView( "result", "left", false )
        } catch ( err ) {
            console.log( err )
            toast( "Something went wrong while checking your answers! Please try again." )
        }
    }


    const { status: streamStatus, error: streamError, expectedTotal } = SimbaAIStore.getQuestionStream
    const totalQuestions = expectedTotal || SimbaAIStore.getPracticeSessionQuestions.length
    const isLastQuestion = SimbaAIStore.getQuizState.questionIndex >= totalQuestions - 1
    const canSubmit = streamStatus !== "streaming" && streamStatus !== "error"

    switch ( SimbaAIStore.getView.current ) {
        case "loading":
            return <AIWaitingScreen />

        case "practice-session-details-fetch-error":
            return (
                <Box padding="20px">
                    <Box position="relative" border={`2px dashed ${palette.borderColor}`} borderRadius="20px" flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" gap="10px" justifyContent="center" >
                        <Box position="absolute" height="60%" maxHeight="400px" bgcolor={palette.contentBg} borderRadius="50%" sx={{ aspectRatio: "1 / 1", zIndex: 1 }} />
                        <Box position="relative" sx={{ zIndex: 10 }} component="img" src={sadSimba} height="180px" alt="No practice sessions" />
                        <Typography position="relative" sx={{ zIndex: 10 }} textAlign="center" variant="subtitle2" component="p" >{SimbaAIStore.getErrors.aiResponse || SimbaAIStore.getErrors.startingPractice || "Something went wrong while trying to generate your response"}</Typography>
                        <Box display="flex" gap={layout.cardsGap} alignItems="center" flexWrap="wrap">
                            <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Exit</Button>
                        </Box>
                    </Box>
                </Box>
            )

        case "question":
            const questionDetails = SimbaAIStore.getPracticeSessionQuestions[SimbaAIStore.getQuizState.questionIndex]
            const questionLoading = !questionDetails
            if ( questionLoading && SimbaAIStore.getQuizState.questionIndex >= totalQuestions ) return null

            return (
                <Box sx={{ overflowX: "hidden", overflowY: "auto" }} display="flex" flexGrow={1} flexDirection="column" justifyContent="flex-start" gap={layout.cardsGap}>

                    <Box flexShrink={0} margin={layout.pagePadding}>
                        <Typography fontWeight="800" gutterBottom fontSize="14px">Question {SimbaAIStore.getQuizState.questionIndex + 1} of {totalQuestions}</Typography>
                        <Box overflow="clip" height="16px" borderRadius="20px" bgcolor={palette.greyed.light + "55"}>
                            <Box
                                className="animation-wave"
                                borderRadius="100px"
                                sx={{
                                    background: palette.primary.dark,
                                    transition: "width 400ms",
                                    position: "relative",
                                    ":before": {
                                        content: `''`,
                                        borderRadius: "100px",
                                        height: "3px",
                                        width: "calc(100% - 10px)",
                                        top: "4px",
                                        left: "5px",
                                        position: "absolute",
                                        background: `#ffffff`,
                                        transition: "width 400ms",
                                        opacity: 0.2
                                    }
                                }}
                                height="100%"
                                width={`${( 100 * ( SimbaAIStore.getQuizState.questionIndex ) ) / totalQuestions}%`}
                            />
                        </Box>
                        {streamStatus === "error" && <Box marginTop="10px" padding="10px" borderRadius="10px" border={border[1]} bgcolor={palette.error.main + "22"}>
                            <Typography variant='subtitle2' color='error'>{streamError || "Failed to generate the remaining questions."}</Typography>
                        </Box>}
                    </Box>

                    <Slide in direction='left' key={SimbaAIStore.getQuizState.questionIndex} >
                        {questionLoading ? (
                            <Box overflow="auto" padding={layout.pagePadding} display="flex" alignItems={{ xs: "center", sm: "flex-start" }} gap="20px" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }}>
                                <Box component="img" sx={{ width: { xs: "20vw", sm: "30%" }, maxWidth: 200 }} src={thinkingSimba} />
                                <Box minWidth={0} width={{ xs: "100%", sm: "auto" }} flexGrow={1} display="flex" justifyContent="center" flexDirection="column" gap="10px">
                                    <Skeleton variant="text" animation="wave" width="90%" height={40} />
                                    <Skeleton variant="text" animation="wave" width="60%" height={40} sx={{ marginBottom: "10px" }} />
                                    <Box flexGrow={1} minWidth={0} display="flex" gap="10px" flexDirection="column">
                                        {[0, 1, 2, 3].map( index => (
                                            <Skeleton key={index} variant="rounded" animation="wave" height={56} width="100%" sx={{ borderRadius: "10px" }} />
                                        ) )}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box overflow="auto" padding={layout.pagePadding} display="flex" alignItems={{ xs: "center", sm: "flex-start" }} gap="20px" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }}>
                                <Box component="img" sx={{ width: { xs: "20vw", sm: "30%" }, maxWidth: 200 }} src={thinkingSimba} />
                                <Box minWidth={0} width={{ xs: "100%", sm: "auto" }} flexGrow={1} display="flex" justifyContent="center" flexDirection="column">
                                    <Typography component="div">
                                        <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: questionDetails.question?.length > 100 ? "16px" : "24px", overflow: "auto", marginBlockStart: 0 }} >
                                            {questionDetails.question}
                                        </Markdown>
                                    </Typography>
                                    {questionDetails.correct_answers?.length > 1 && <Box marginBottom="20px">
                                        <CustomChip startIcon={Icons.default.Info} variant='filled' color="customThemeColor" value="Choose multiple correct answers." />
                                    </Box>}
                                    <Box flexGrow={1} minWidth={0} width="100%" display="flex" gap="10px" flexDirection="column">
                                        {questionDetails.answers.map( ( option, index ) => {

                                            const selected = SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] === option || ( Array.isArray( SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ) && SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex]?.includes( option ) )

                                            return (
                                                <Box
                                                    component="button"
                                                    width="100%"
                                                    maxWidth="100%"
                                                    boxSizing="border-box"
                                                    minWidth={0}
                                                    className={selected ? "animation-wave" : ""}
                                                    padding={{ md: "10px 20px", xs: "10px" }}
                                                    borderRadius="10px"
                                                    border={`2px solid ${palette.borderColor}`}
                                                    borderBottom={`4px solid ${palette.borderColor}`}
                                                    color={palette.text.secondary}
                                                    key={option}
                                                    display="flex"
                                                    gap="10px"
                                                    alignItems="flex-start"
                                                    sx={{
                                                        background: `${selected ? theme === 'dark' ? palette.primary.main + "44" : palette.primary.main : "none"}`,
                                                        transition: "background 200ms",
                                                        borderColor: selected ? palette.primary.light : palette.borderColor,
                                                        color: selected ? palette.common.white : palette.text.secondary,
                                                        cursor: "pointer",
                                                        ":hover": {
                                                            borderColor: selected ? palette.primary.light : palette.primary.light,
                                                            color: selected ? palette.common.white : palette.text.primary
                                                        }
                                                    }}
                                                    onClick={() => selectAnswer( option )}
                                                >
                                                    {questionDetails.correct_answers?.length > 1 && <Box marginLeft="-10px" textAlign="left" flexShrink={0} display="flex" alignItems='center' justifyContent="center" color='inherit' component="span" height="30px" borderRadius="50%" >
                                                        <Checkbox
                                                            sx={{
                                                                color: "GrayText",
                                                                '&.Mui-checked': {
                                                                    color: "white",
                                                                },
                                                            }}
                                                            checked={Boolean( selected ) || false} />
                                                    </Box>}
                                                    {questionDetails.correct_answers?.length === 1 && <Box marginLeft="-10px" textAlign="left" flexShrink={0} display="flex" alignItems='center' justifyContent="center" color='inherit' component="span" height="30px" borderRadius="50%" >
                                                        <Radio
                                                            sx={{
                                                                color: "GrayText",
                                                                '&.Mui-checked': {
                                                                    color: "white",
                                                                },
                                                            }}
                                                            name={`question-${SimbaAIStore.getQuizState.questionIndex}`}
                                                            value={`option-${index}`}
                                                            checked={Boolean( selected ) || false}
                                                        />
                                                    </Box>}
                                                    <Box minWidth={0} flexGrow={1}>
                                                        <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: questionDetails.question?.length > 100 ? "16px" : "18px", overflow: "auto", marginBlockStart: 0, textAlign: "left", color: "inherit", "& p": { marginBottom: "0 !important" } }} >
                                                            {option}
                                                        </Markdown>
                                                    </Box>
                                                </Box>
                                            )
                                        } )}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Slide>
                    <Box position="sticky" bottom="0" display="flex" justifyContent="space-between" alignItems="center" borderTop={`4px dashed ${palette.borderColor}`} bgcolor={palette.contentBg} padding="30px 20px">
                        <Box display="flex" gap="10px" alignItems="center">
                            <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' color='error' >Quit Practice</Button>
                            <ReportIssue key={SimbaAIStore.getQuizState.questionIndex} />
                        </Box>
                        <Box display="flex" gap="10px" alignItems="center">
                            {!isLastQuestion && <Button
                                disabled={
                                    !SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ||
                                    ( questionDetails?.correct_answers?.length > 1 && SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex].length < 2 )
                                }
                                onClick={nextQuestion}
                                sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }}
                                endIcon={Icons.default.ArrowForward}
                                variant='contained'
                                disableElevation
                                color='primary'
                            >
                                Next
                            </Button>}
                            {isLastQuestion && <Button
                                disabled={
                                    !canSubmit ||
                                    !SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ||
                                    ( questionDetails?.correct_answers?.length > 1 && SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex].length < 2 )
                                }
                                className={SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ? 'animation-wave' : ""}
                                onClick={submitAndViewScores}
                                sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }}
                                startIcon={streamStatus === "streaming" ? <CircularProgress size={20} /> : Icons.default.CheckIcon}
                                variant='contained'
                                disableElevation
                                color='primary'
                            >
                                {streamStatus === "streaming" ? "Finalizing" : "Submit"}
                            </Button>}
                        </Box>
                    </Box>
                </Box>
            )

        case "result":
            return ( <Box display="flex" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }} justifyContent="center" alignItems="center" gap="20px">

                <Confetti mode="boom" particleCount={150} />

                <Box component="img" width="25vw" src={celebratingSimba} />
                <Box>
                    <Typography textAlign={{ xs: "center", sm: "left" }} fontSize="18px" fontWeight={800}>Great! Quiz Completed</Typography>
                    <Typography textAlign={{ xs: "center", sm: "left" }} fontSize="32px" fontWeight={800}>You got {SimbaAIStore.getQuizState.attemptDetails.score} out of {SimbaAIStore.getPracticeSessionQuestions.length} questions.</Typography>
                    <Box flexWrap="wrap" justifyContent={{ xs: "center", sm: "left" }} flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                        <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='contained' disableElevation color='error' >Finish</Button>
                        <Button onClick={() => SimbaAIStore.setView( "answers" )} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} className='animation-wave' endIcon={Icons.default.ArrowRight} variant='contained' disableElevation color='primary' >View Answers</Button>
                    </Box>
                </Box>
            </Box> )


        case "answers":
            return ( <Box minHeight={0} display="flex" padding={layout.pagePadding} flexGrow={1} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap={layout.cardsGap}>
                <Box flexShrink={0} width="100%" padding={layout.pagePadding} borderRadius="10px" gap={layout.cardsGap} alignItems="center" display="flex" sx={{
                    background: palette.primary.light
                }} >
                    <Box component="img" height="120px" src={celebratingSimba} />
                    <Box>
                        <Typography color={palette.common.white} fontSize="24px" fontWeight={600}>You got {SimbaAIStore.getQuizState.attemptDetails.score} out of {SimbaAIStore.getQuizState.attemptDetails.questions.length} questions.</Typography>
                        <Typography color={palette.common.white} fontSize="12px" fontWeight={800}>Check the answers below!</Typography>

                        <Box flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} marginTop="10px" display="flex" gap={layout.cardsGap} alignItems="center" >
                            {SimbaAIStore.getQuizState.previousContent !== "history" && <Button onClick={backToHome} sx={{ color: palette.primary.main, bgcolor: palette.common.white, border: `1px solid ${palette.grey[500]}`, borderBottom: `4px solid ${palette.grey[500]}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='contained' disableElevation color='error' >Finish</Button>}
                        </Box>
                    </Box>
                </Box>
                <Box flexGrow={1} width="100%" display="flex" minHeight={0} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" overflow="auto" gap="30px">
                    {SimbaAIStore.getQuizState.attemptDetails.questions.map( ( question, qIndex ) => {

                        let isCorrect

                        let provided
                        const correctAnswers = new Set( question.correct_answers )
                        if ( question.correct_answers?.length > 1 ) {
                            provided = new Set( SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] )
                        } else {
                            provided = new Set( [SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex]] )
                        }

                        const difference = provided.symmetricDifference( correctAnswers )

                        isCorrect = difference.size === 0

                        const issueReoprtDetails = {
                            ...question,
                            selected_answer: SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex],
                        }

                        return (
                            <Box width="100%" flexShrink={0} key={qIndex} flexGrow={1} display="flex" gap="10px" flexDirection="column">

                                {qIndex !== 0 && <Divider />}

                                <Box display="flex" justifyContent="space-between" flexDirection={{ xs: "column", md: "row" }} alignItems="flex-end">
                                    <Box>
                                        <Typography color='textSecondary' fontWeight={600} fontSize="12px" gutterBottom>Question {qIndex + 1}</Typography>
                                        <Typography component="div">
                                            <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                                {`${question.question}`}
                                            </Markdown>
                                        </Typography>
                                    </Box>
                                    <ReportIssue key={qIndex} questionIndex={qIndex} attemptDetails={SimbaAIStore.getQuizState.attemptDetails} questionDetails={issueReoprtDetails} />
                                </Box>

                                <Box display="flex" flexDirection="column" gap="10px" borderRadius={layout.borderRadius}>

                                    {/* <Box padding="10px" borderRadius={layout.borderRadius} display="flex" border={border[1]} justifyContent="space-between" gap="10px"> */}
                                    <Typography variant='subtitle2' display="flex" alignItems="center" gap="5px" color={isCorrect ? "success" : "error"} component="p"> <Typography component="span" color={isCorrect ? "success" : "error"} display="flex" alignItems="center">{isCorrect ? Icons.default.CheckCircleIcon : Icons.default.CancelIcon}</Typography>{isCorrect ? "Correct" : "Incorrect"}</Typography>
                                    {/* </Box> */}

                                    <Box borderRadius={layout.borderRadius} display="flex" flexDirection="column" gap="5px">
                                        {question.answers.map( ( option, index ) => {

                                            const selected = SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] === option || ( Array.isArray( SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] ) && SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex]?.includes( option ) )
                                            const inCorrectAnswersList = question.correct_answers.includes( option )

                                            return (
                                                <Box key={index} borderRadius={layout.borderRadius} bgcolor={selected ? inCorrectAnswersList ? palette.success.dark + 33 : palette.error.dark + 22 : "none"} border={border[1]} display="flex" paddingX="10px" alignItems="flex-start">
                                                    <Typography marginTop="10px" sx={{ color: "GrayText" }} fontSize="20px">{selected ? Icons.default.CheckboxChecked : Icons.default.Checkbox}</Typography>
                                                    <Box minWidth={0} flexGrow={1} padding="10px">
                                                        <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", fontWeight: inCorrectAnswersList ? 500 : 400, marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& p": { marginBottom: "0 !important" }, "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                                            {option}
                                                        </Markdown>
                                                    </Box>
                                                    {inCorrectAnswersList && <Typography marginTop="10px" color='success' display="flex" alignItems="center" fontSize="20px" component="span">{Icons.default.CheckCircleIconOutlined}</Typography>}
                                                    {!inCorrectAnswersList && selected && <Typography marginTop="10px" color='error' display="flex" alignItems="center" fontSize="20px" component="span">{Icons.default.CancelIcon}</Typography>}
                                                </Box>
                                            )
                                        } )}
                                    </Box>

                                    <Box padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} border={border[1]} borderLeft={`4px solid ${palette.primary.main}`} >
                                        <Typography fontSize="14px" gap="10px" marginBottom="10px" fontWeight={600} display="flex" alignItems="center" color='primary'> {Icons.default.AskAI} SIMBA AI EXPLAINS</Typography>
                                        <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& p": { marginBottom: "0 !important" }, "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                            {question.explanation}
                                        </Markdown>
                                    </Box>

                                </Box>
                            </Box>
                        )
                    } )}
                </Box>
            </Box> )

        default:
            return null
    }
} )


const SamplePracticeModeRendererForFaculty = observer( ( { closeAiMentorDialog } ) => {

    const SimbaAIStore = useSimbaAIStore()

    return (
        <Box className="slim-custom-scrollbar" flexGrow={1} display="flex" flexDirection="column" overflow="auto" position="relative" gap="10px" >

            <TransitionGroup style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <Fade
                    key={SimbaAIStore.getView.current}
                    timeout={{ enter: 100, exit: 0 }}
                >
                    <Slide
                        in
                        direction={SimbaAIStore.getView.slideDirection || "left"}
                        timeout={{ enter: 350, exit: 0 }}
                    >
                        <Box minHeight={0} flexGrow={1} overflow="auto" display="flex" flexDirection="column">
                            <Renderer closeAiMentorDialog={closeAiMentorDialog} />
                        </Box>
                    </Slide>
                </Fade>
            </TransitionGroup>
        </Box>
    )

} )

export default SamplePracticeModeRendererForFaculty